import React, { useEffect, useMemo, useState } from "react";
import AppLayout from "../components/AppLayout";
import StorageFileLink from "../components/StorageFileLink";
import { supabase } from "../lib/supabase";
import { Building2, ExternalLink, FileText, Image, ReceiptText, Search, User, Wrench } from "lucide-react";

function Input({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={`flex w-full rounded-2xl border px-4 py-3 text-sm outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 ${className}`}
    />
  );
}

function Badge({ children, className = "" }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${className}`}>
      {children}
    </span>
  );
}

function parseLinks(value) {
  if (!value) return [];

  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed;
  } catch (_error) {
    return String(value)
      .split(/\n|,/)
      .map((item) => item.trim())
      .filter(Boolean)
      .map((url) => ({ name: url.split("/").pop(), url }));
  }

  return [];
}

function getFileType(url = "") {
  const lower = url.toLowerCase();
  if (lower.match(/\.(png|jpg|jpeg|webp|gif)(\?|$)/)) return "Image";
  if (lower.match(/\.pdf(\?|$)/)) return "PDF";
  if (lower.match(/\.(doc|docx)(\?|$)/)) return "Document";
  if (lower.match(/\.(xls|xlsx|csv)(\?|$)/)) return "Spreadsheet";
  return "File";
}

function typeIcon(type) {
  if (type === "Property Image") return Building2;
  if (type === "Tenant Image") return User;
  if (type === "Vendor") return Wrench;
  if (type === "Expense") return ReceiptText;
  if (type === "Image") return Image;
  return FileText;
}

export default function Documents({ isDark, setIsDark, currency, setCurrency }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");

  const fetchDocuments = async () => {
    setLoading(true);

    const [
      { data: propertyData, error: propertyError },
      { data: tenantData, error: tenantError },
      { data: expenseData, error: expenseError },
      { data: ticketData, error: ticketError },
      { data: vendorData, error: vendorError },
    ] = await Promise.all([
      supabase.from("properties").select("id, name, property_image_url"),
      supabase.from("tenants").select("id, full_name, property_name, tenant_image_url, supporting_documents"),
      supabase.from("expenses").select("id, property_name, vendor, supporting_files"),
      supabase.from("maintenance_tickets").select("id, issue, property, supporting_files"),
      supabase.from("vendors").select("id, name, company, service_category, supporting_files"),
    ]);

    if (propertyError) console.error("Error fetching property documents:", propertyError);
    if (tenantError) console.error("Error fetching tenant documents:", tenantError);
    if (expenseError) console.error("Error fetching expense documents:", expenseError);
    if (ticketError) console.error("Error fetching maintenance documents:", ticketError);
    if (vendorError) console.error("Error fetching vendor documents:", vendorError);

    const nextDocuments = [];

    (propertyData || []).forEach((property) => {
      if (property.property_image_url) {
        nextDocuments.push({
          id: `property-${property.id}`,
          name: `${property.name} image`,
          owner: property.name,
          type: "Property Image",
          fileType: "Image",
          url: property.property_image_url,
          source: "Properties",
        });
      }
    });

    (tenantData || []).forEach((tenant) => {
      if (tenant.tenant_image_url) {
        nextDocuments.push({
          id: `tenant-image-${tenant.id}`,
          name: `${tenant.full_name} photo`,
          owner: tenant.full_name,
          type: "Tenant Image",
          fileType: "Image",
          url: tenant.tenant_image_url,
          source: tenant.property_name || "Tenants",
        });
      }

      parseLinks(tenant.supporting_documents).forEach((url, index) => {
        const link = typeof url === "string" ? { url, name: url.split("/").pop() } : url;
        nextDocuments.push({
          id: `tenant-doc-${tenant.id}-${index}`,
          name: link.name || `Tenant document ${index + 1}`,
          owner: tenant.full_name,
          type: "Tenant Document",
          fileType: getFileType(link.url),
          url: link.url,
          source: tenant.property_name || "Tenants",
        });
      });
    });

    (expenseData || []).forEach((expense) => {
      parseLinks(expense.supporting_files).forEach((file, index) => {
        nextDocuments.push({
          id: `expense-${expense.id}-${index}`,
          name: file.name || `Expense file ${index + 1}`,
          owner: expense.vendor || expense.property_name,
          type: "Expense",
          fileType: getFileType(file.url),
          url: file.url,
          source: expense.property_name,
        });
      });
    });

    (ticketData || []).forEach((ticket) => {
      parseLinks(ticket.supporting_files).forEach((file, index) => {
        nextDocuments.push({
          id: `maintenance-${ticket.id}-${index}`,
          name: file.name || `Maintenance file ${index + 1}`,
          owner: ticket.issue,
          type: "Maintenance",
          fileType: getFileType(file.url),
          url: file.url,
          source: ticket.property,
        });
      });
    });

    (vendorData || []).forEach((vendor) => {
      parseLinks(vendor.supporting_files).forEach((file, index) => {
        nextDocuments.push({
          id: `vendor-${vendor.id}-${index}`,
          name: file.name || `Vendor document ${index + 1}`,
          owner: vendor.name,
          type: "Vendor",
          fileType: getFileType(file.url),
          url: file.url,
          source: vendor.company || vendor.service_category || "Vendors",
        });
      });
    });

    setDocuments(nextDocuments);
    setLoading(false);
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  const filteredDocuments = useMemo(() => {
    return documents.filter((document) => {
      const matchesQuery = `${document.name} ${document.owner} ${document.source} ${document.type}`
        .toLowerCase()
        .includes(query.toLowerCase());
      const matchesType = typeFilter === "All" || document.type === typeFilter;
      return matchesQuery && matchesType;
    });
  }, [documents, query, typeFilter]);

  const typeOptions = ["All", "Property Image", "Tenant Image", "Tenant Document", "Expense", "Maintenance", "Vendor"];

  return (
    <AppLayout
      isDark={isDark}
      setIsDark={setIsDark}
      currency={currency}
      setCurrency={setCurrency}
      eyebrow="File Library"
      title="Documents"
      description="Find uploaded property images, tenant files, expense receipts, supplier invoices, and supporting documents."
      actions={
        <button
          type="button"
          onClick={fetchDocuments}
          className="inline-flex h-10 items-center justify-center rounded-xl bg-orange-500 px-3 text-sm font-medium text-white transition hover:bg-orange-600"
        >
          Refresh
        </button>
      }
    >
      <section className="grid gap-4 md:grid-cols-2 2xl:grid-cols-4">
        {[
          { label: "All files", value: documents.length, tone: "bg-sky-100 text-sky-700" },
          { label: "Tenant files", value: documents.filter((item) => item.type.includes("Tenant")).length, tone: "bg-emerald-100 text-emerald-700" },
          { label: "Work files", value: documents.filter((item) => ["Expense", "Maintenance", "Vendor"].includes(item.type)).length, tone: "bg-orange-100 text-orange-700" },
          { label: "Images", value: documents.filter((item) => item.fileType === "Image").length, tone: "bg-indigo-100 text-indigo-700" },
        ].map((item) => (
          <div key={item.label} className={`rounded-[2rem] border p-5 shadow-md ${isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
            <p className={`text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>{item.label}</p>
            <div className="mt-3 flex items-center justify-between">
              <h3 className={`text-3xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>{item.value}</h3>
              <Badge className={item.tone}>Live</Badge>
            </div>
          </div>
        ))}
      </section>

      <section className={`rounded-[2rem] border p-4 shadow-sm md:p-5 ${isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          <div className="relative w-full max-w-md">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search documents..."
              className={`h-12 pl-11 pr-4 ${isDark ? "border-slate-700 bg-slate-950 text-white" : "border-slate-200 bg-white text-slate-800"}`}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {typeOptions.map((type) => (
              <button
                key={type}
                onClick={() => setTypeFilter(type)}
                className={`rounded-2xl px-4 py-2 text-sm font-medium transition ${
                  typeFilter === type
                    ? "bg-sky-100 text-sky-700"
                    : isDark
                    ? "bg-slate-800 text-slate-300 hover:bg-slate-700"
                    : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className={`rounded-[2rem] border p-5 shadow-sm ${isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
        {loading ? (
          <div className={isDark ? "text-slate-300" : "text-slate-600"}>Loading documents...</div>
        ) : filteredDocuments.length === 0 ? (
          <div className={isDark ? "text-slate-300" : "text-slate-600"}>No documents found.</div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
            {filteredDocuments.map((document) => {
              const Icon = typeIcon(document.type === "Tenant Document" ? document.fileType : document.type);

              return (
                <StorageFileLink
                  key={document.id}
                  reference={document.reference || document.url}
                  target="_blank"
                  rel="noreferrer"
                  className={`rounded-3xl border p-4 transition ${
                    isDark
                      ? "border-slate-800 bg-slate-950 hover:bg-slate-800"
                      : "border-slate-200 bg-slate-50 hover:bg-white"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="rounded-2xl bg-sky-100 p-3 text-sky-700">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="min-w-0">
                        <p className={`truncate text-sm font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>{document.name}</p>
                        <p className={`mt-1 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>{document.owner}</p>
                      </div>
                    </div>
                    <ExternalLink className="h-4 w-4 shrink-0 text-slate-400" />
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Badge className="bg-sky-100 text-sky-700">{document.type}</Badge>
                    <Badge className="bg-slate-200 text-slate-700">{document.fileType}</Badge>
                  </div>
                  <p className={`mt-3 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>{document.source}</p>
                </StorageFileLink>
              );
            })}
          </div>
        )}
      </section>
    </AppLayout>
  );
}
